<script setup lang="ts">
import { ref, onMounted } from 'vue';
import EmblaCarousel from 'embla-carousel';
import gettext from '@/i18n/gettext';
import { INFO_ROBCO_BASE_URL } from '@/Constants';

const { $gettext } = gettext;

const emblaRef = ref<HTMLElement | null>(null);
const emblaApi = ref<ReturnType<typeof EmblaCarousel> | null>(null);

const videos = [
  {
    src: `${INFO_ROBCO_BASE_URL}/hubfs/RobCo.Studio/3d-Scan.mp4`,
    title: $gettext('Step 1: 3D Scan'),
  },
  {
    src: `${INFO_ROBCO_BASE_URL}/hubfs/RobCo.Studio/Simulation.mp4`,
    title: $gettext('Step 2: Simulation'),
  },
  {
    src: 'https://8591354.fs1.hubspotusercontent-na1.net/hubfs/8591354/RobCo.Studio/deloyment.mp4',
    title: $gettext('Step 3: Deployment'),
  },
];

onMounted(() => {
  if (emblaRef.value) {
    emblaApi.value = EmblaCarousel(emblaRef.value, {
      align: 'center',
    });
  }
});

function scrollPrev() {
  emblaApi.value?.scrollPrev();
}

function scrollNext() {
  emblaApi.value?.scrollNext();
}
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <h1 class="mb-16">
      {{ $gettext("How it's made") }}
    </h1>

    <div>
      <section class="embla">
        <div ref="emblaRef" class="embla__viewport">
          <div class="embla__container">
            <div v-for="(video, i) in videos" :key="i" class="embla__slide">
              <h2 class="slide-title">{{ video.title }}</h2>
              <div class="video-box">
                <video
                  class="video-el"
                  :title="video.title"
                  :src="video.src"
                  preload="metadata"
                  muted
                  playsinline
                  controls
                />
              </div>
            </div>
          </div>
        </div>
        <v-btn
          class="embla__button embla__button--prev"
          icon="mdi-chevron-left"
          variant="elevated"
          size="large"
          aria-label="Previous"
          @click="scrollPrev"
        />
        <v-btn
          class="embla__button embla__button--next"
          icon="mdi-chevron-right"
          variant="elevated"
          size="large"
          aria-label="Next"
          @click="scrollNext"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.embla {
  position: relative;
  width: 100%;
  margin: 0 auto;
  overflow: visible;
  --slide-spacing: 1.5rem;
  --slide-size: 90%;
  max-width: 1500px;
}

.embla__viewport {
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.embla__container {
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(var(--slide-spacing) / -2);
  margin-right: calc(var(--slide-spacing) / -2);
  padding-left: calc(var(--slide-spacing) / 2);
  padding-right: calc(var(--slide-spacing) / 2);
  align-items: center;
}

.embla__slide {
  flex: 0 0 var(--slide-size);
  min-width: 0;
  padding: 0 calc(var(--slide-spacing) / 2);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.slide-title {
  margin-bottom: 12px;
  text-align: center;
}

.video-box {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  max-width: 1300px;
}

.video-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.embla__button {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.embla__button:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: translateY(-50%) scale(1.05);
}

.embla__button--prev {
  left: -16px;
}

.embla__button--next {
  right: -16px;
}
</style>
